import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { SupergridGroupTargetsGeosDocument, GeoType } from 'wb-spotify/graphql/generated';

export type GeoOption = {
	value: string;
	label: string;
	__typename?: string;
};

/**
 * Hook to fetch geo labels from IDs
 * @param geoIds - Array of geo IDs to fetch labels for
 * @param geoType - Type of geo (City, DmaRegion, Region, PostalCode)
 * @returns Array of geo options with labels, loading state, and error
 */
export function useGeoLabels(geoIds: string[] | undefined, geoType: GeoType) {
	const [loadGeos, { data, loading, error }] = useLazyQuery(SupergridGroupTargetsGeosDocument);
	const [cachedLabels, setCachedLabels] = useState<Map<string, string>>(new Map());

	const geoOptions = useMemo(() => {
		if (!geoIds || geoIds.length === 0) {
			return [];
		}

		// If we have cached labels, use them
		const options: GeoOption[] = geoIds.map(id => {
			const cachedLabel = cachedLabels.get(id);

			return {
				value:      id,
				label:      cachedLabel || id, // Use cached label or ID as fallback
				__typename: 'Geo',
			};
		});

		return options;
	}, [geoIds, cachedLabels]);

	useEffect(() => {
		if (!geoIds || geoIds.length === 0) {
			return;
		}

		// Filter out IDs we already have cached
		const uncachedIds = geoIds.filter(id => !cachedLabels.has(id));

		if (uncachedIds.length === 0) {
			return;
		}

		// Build query to fetch specific geo IDs
		const geoIdFilter = uncachedIds.map(id => `geoId = '${id}'`).join(' OR ');

		loadGeos({
			variables: {
				query:   `(${geoIdFilter})`,
				filters: {
					geoType: [
						{
							value:   [geoType],
							exclude: false,
						},
					],
				},
				limit: uncachedIds.length,
			},
		});
	}, [geoIds, geoType, loadGeos, cachedLabels]);

	// Update cache when data arrives
	useEffect(() => {
		if (data?.geos?.items) {
			setCachedLabels(prev => {
				const newCache = new Map(prev);
				// @ts-ignore
				data.geos.items.forEach(item => {
					if (item?.value) {
						newCache.set(item.value, item.label);
					}
				});

				return newCache;
			});
		}
	}, [data]);

	return {
		geoOptions,
		loading,
		error,
	};
}

/**
 * Helper function to extract geo IDs from params
 * Handles both string arrays (from backend) and object arrays (already transformed)
 */
export function extractGeoIds(
	params: any,
	fieldName: 'cityIds' | 'dmaIds' | 'regionIds' | 'postalCodeIds',
): string[] | undefined {
	const value = params.data?.groupTargets?.groupTargetsGeoTargets?.[fieldName];

	if (!value || !Array.isArray(value)) {
		return undefined;
	}

	// If already objects with value property, extract IDs
	if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'value' in value[0]) {
		return value.map((item: any) => item.value).filter(Boolean);
	}

	// If strings, return as is
	if (typeof value[0] === 'string') {
		return value.filter(Boolean);
	}

	return undefined;
}
